import { useState, useCallback } from 'react'
import useOrganizationSubscriptionItems from 'hooks/use-organization-subscription-items'
import { StripeSubscriptionItem } from 'interfaces/stripe-subscription-item'

interface UseOrgSubscriptionItemsProps {
  orgId: string
}

export const useOrgSubscriptionItems = ({
  orgId,
}: UseOrgSubscriptionItemsProps) => {
  // Fetch current subscription items for the organization
  const {
    data: subscriptionItems,
    isLoading: isLoadingSubscriptionItems,
    error: subscriptionItemsError,
    refetch: refetchSubscriptionItems,
  } = useOrganizationSubscriptionItems(orgId)

  // Local state for managing updates to subscription items
  const [updatedItems, setUpdatedItems] = useState<StripeSubscriptionItem[]>([])

  // Initialize updated items when subscription items are loaded
  const initializeUpdatedItems = useCallback(() => {
    if (subscriptionItems && subscriptionItems.length > 0) {
      setUpdatedItems([...subscriptionItems])
    }
  }, [subscriptionItems])

  // Check if there are any changes
  const hasChanges = useCallback(() => {
    if (
      !subscriptionItems ||
      subscriptionItems.length !== updatedItems.length
    ) {
      return true
    }

    return subscriptionItems.some((originalItem, index) => {
      const updatedItem = updatedItems[index]
      return (
        originalItem.quantity !== updatedItem.quantity ||
        originalItem.price_id !== updatedItem.price_id ||
        originalItem.product_id !== updatedItem.product_id
      )
    })
  }, [subscriptionItems, updatedItems])

  return {
    // Original subscription items from API
    originalSubscriptionItems: subscriptionItems,
    isLoading: isLoadingSubscriptionItems,
    error: subscriptionItemsError,
    refetch: refetchSubscriptionItems,

    // Local state for updates
    updatedSubscriptionItems: updatedItems,

    // Actions
    initializeUpdatedItems,
    hasChanges,
  }
}
